import { useState, useEffect, useRef } from "react";
import {
  loadCaptchaScript,
  reportCaptchaFailure,
  type CaptchaFailureStage,
} from "utils/captchaScript";
import { sharedLib } from "@shared/services/shared.service";
import { FixMeLater } from "types";
import { openMessage, useIdle } from "utils/misc";
import moment, { MomentInput } from "moment";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";

export interface UseGeetestProps {
  onSuccess: (captchaToken: string) => void;
  onError?: (message?: string) => void;
  onClose?: () => void;
  captchaErrorMessage?: string;
  maxRefetchErrorMessage?: string;
  maxNumCaptchaRefetch?: number;
  idleTimeoutMs?: number;
  skipIdleRefetch?: boolean;
}

export const useGeetest = ({
  onSuccess,
  onError,
  onClose,
  captchaErrorMessage = DEFAULT_CAPTCHA_ERROR_MESSAGE,
  maxRefetchErrorMessage = DEFAULT_MAX_REFETCH_ERROR_MESSAGE,
  maxNumCaptchaRefetch = DEFAULT_MAX_NUM_CAPTCHA_REFETCH,
  idleTimeoutMs = 1000 * 60 * 2,
  skipIdleRefetch = true,
}: UseGeetestProps) => {
  const { isWhitelabel } = useBranding();
  const [scriptReady, setScriptReady] = useState(false);
  const [scriptFailed, setScriptFailed] = useState(false);
  const loadScript = () =>
    loadCaptchaScript("/gt.js")
      .then(() => {
        setScriptReady(true);
        setScriptFailed(false);
      })
      .catch(() => {
        reportCaptchaFailure("geetest", "script_load");
        setScriptFailed(true);
        setIsVerifying(false);
        openMessage(captchaErrorMessage, "error");
        onError?.(captchaErrorMessage);
      });
  useEffect(() => {
    void loadScript();
  }, []);
  const {
    data: captchaData,
    isFetching: isFetchingGetCaptcha,
    isError: getCaptchaError,
    error: captchaInitializationError,
    refetch: refetchGetCaptcha,
  } = sharedLib.sharedApi.authentication.useGetCaptchaQuery({
    is_whitelabel: isWhitelabel,
  });

  const [
    validateCaptcha,
    {
      isFetching: isLoadingValidateCaptcha,
      data: validateCaptchaData,
      error: validationError,
    },
  ] = sharedLib.sharedApi.authentication.useLazyValidateCaptchaQuery();

  const [captchaObj, setCaptchaObj] = useState<FixMeLater>();
  const numCaptchaRefetchRef = useRef(0);
  const [isVerifying, setIsVerifying] = useState(false);
  const [submitClicks, setSubmitClicks] = useState(0);
  const lastMessageDisplayedRef = useRef<MomentInput>(undefined);
  const idle = useIdle(idleTimeoutMs);

  const resetAndRefetchCaptcha = () => {
    if (captchaObj) {
      captchaObj.reset();
      setCaptchaObj(undefined);
    }

    if (numCaptchaRefetchRef.current >= maxNumCaptchaRefetch) {
      if (
        !lastMessageDisplayedRef.current ||
        moment().diff(lastMessageDisplayedRef.current, "seconds") > 5
      ) {
        openMessage(maxRefetchErrorMessage, "error");
        reportCaptchaFailure("geetest", "retry_exhausted");
        lastMessageDisplayedRef.current = moment();
      }
    } else {
      numCaptchaRefetchRef.current = numCaptchaRefetchRef.current + 1;
      refetchGetCaptcha();
    }
  };

  const handleLocalError = (
    message?: string,
    stage: CaptchaFailureStage = "missing_proof"
  ) => {
    reportCaptchaFailure("geetest", stage);
    if (message) {
      openMessage(message, "error");
    }
    setIsVerifying(false);
    resetAndRefetchCaptcha();
    onError?.(message);
  };

  useEffect(() => {
    if (!idle && !skipIdleRefetch) {
      numCaptchaRefetchRef.current = 0;
      resetAndRefetchCaptcha();
    }
  }, [idle, skipIdleRefetch]);

  useEffect(() => {
    if (scriptReady && !isFetchingGetCaptcha && captchaData) {
      (window as FixMeLater).initGeetest(
        {
          gt: captchaData.gt,
          challenge: captchaData.challenge,
          offline: !captchaData.success,
          product: "bind",
          new_captcha: true,
          https: true,
          lang: "en",
          api_server_v3: ["api.geetest.com", "api.geevisit.com"],
        },
        (_captchaObj: FixMeLater) => {
          _captchaObj.onReady(() => {
            setCaptchaObj(_captchaObj);
          });
          _captchaObj.onSuccess(() => {
            const result = _captchaObj.getValidate();
            if (result) {
              validateCaptcha({
                challenge: result.geetest_challenge,
                validate: result.geetest_validate,
                seccode: result.geetest_seccode,
                is_whitelabel: isWhitelabel,
              });
            } else {
              handleLocalError(captchaErrorMessage);
            }
          });
          _captchaObj.onClose(() => {
            setIsVerifying(false);
            onClose?.();
          });
          _captchaObj.onError(() => {
            handleLocalError(undefined, "widget_error");
          });
        }
      );
    }
  }, [scriptReady, captchaData, isFetchingGetCaptcha]);

  useEffect(() => {
    if (validateCaptchaData) {
      if (validateCaptchaData.success && validateCaptchaData.captcha_token) {
        onSuccess(validateCaptchaData.captcha_token);
        setIsVerifying(false);
      } else {
        handleLocalError(captchaErrorMessage, "validation_rejected");
      }
    }
  }, [validateCaptchaData]);

  useEffect(() => {
    if (captchaInitializationError) {
      const status =
        typeof captchaInitializationError === "object" &&
        "status" in captchaInitializationError
          ? captchaInitializationError.status
          : undefined;
      reportCaptchaFailure(
        "geetest",
        "initialization_request",
        undefined,
        status
      );
    }
  }, [captchaInitializationError]);

  useEffect(() => {
    if (validationError) {
      const status =
        typeof validationError === "object" && "status" in validationError
          ? validationError.status
          : undefined;
      reportCaptchaFailure("geetest", "validation_request", undefined, status);
    }
  }, [validationError]);

  const verify = () => {
    if (scriptFailed) void loadScript();
    setSubmitClicks((prev) => prev + 1);
    setIsVerifying(true);

    if (getCaptchaError) {
      resetAndRefetchCaptcha();
    }
  };

  useEffect(() => {
    if (submitClicks > 0 && captchaObj) {
      captchaObj.verify();
    }
  }, [submitClicks, captchaObj]);

  return {
    captcha: null,
    verify,
    isLoading:
      isFetchingGetCaptcha ||
      isLoadingValidateCaptcha ||
      (isVerifying && !captchaObj),
  };
};

const DEFAULT_MAX_NUM_CAPTCHA_REFETCH = 10;
const DEFAULT_CAPTCHA_ERROR_MESSAGE =
  "Something went wrong when validating captcha information, please try again";
const DEFAULT_MAX_REFETCH_ERROR_MESSAGE =
  "Something went wrong when fetching the captcha information. Please refresh the page and try again";
